// ============================================================================
// FNE-BRIDGE Frontend - Layout principal (sidebar + header + content)
// i18n, logo FNE, sélecteur de langue, notifications, menu utilisateur
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Drawer, AppBar, Toolbar, IconButton, Typography, Avatar,
  List, ListItemButton, ListItemIcon, ListItemText, Collapse,
  Divider, Badge, Tooltip, Menu, MenuItem, useMediaQuery, useTheme,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import StorageIcon from '@mui/icons-material/Storage';
import TransformIcon from '@mui/icons-material/Transform';
import SyncIcon from '@mui/icons-material/Sync';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CallReceivedIcon from '@mui/icons-material/CallReceived';

import { LAYOUT } from '../config/constants';
import logoFneBridge from '../assets/images/logo-fne-bridge.png';
import { MAIN_NAV, PROTECTED_ROUTES, type NavItem } from '../config/routes';
import { AVAILABLE_LANGUAGES } from '../config/i18n';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useThemeMode } from '../hooks/useThemeMode';
import { useAuthStore } from '../stores';
import { notificationService } from '../services/notificationService';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// --- Icon map ---
const ICON_MAP: Record<string, React.ReactElement> = {
  Dashboard: <DashboardIcon />,
  Receipt: <ReceiptIcon />,
  ConfirmationNumber: <ConfirmationNumberIcon />,
  Storage: <StorageIcon />,
  Transform: <TransformIcon />,
  Sync: <SyncIcon />,
  People: <PeopleIcon />,
  Inventory: <InventoryIcon />,
  Category: <CategoryIcon />,
  ManageAccounts: <ManageAccountsIcon />,
  Settings: <SettingsIcon />,
  HelpOutline: <HelpOutlineIcon />,
  CallReceived: <CallReceivedIcon />,
};

// --- Nav label i18n key map ---
const NAV_I18N_MAP: Record<string, string> = {
  '/': 'nav.dashboard',
  '/stickers': 'nav.stickers',
  '/invoices': 'nav.invoices',
  '/clients': 'nav.clients_and_suppliers',
  '/settings': 'nav.settings',
  '/users': 'nav.users',
  '/articles': 'nav.nomenclature',
  '/connections': 'nav.connections',
  '/sync': 'nav.sync',
  '/help': 'nav.help',
};

const CHILD_I18N_MAP: Record<string, string> = {
  '/invoices': 'nav.invoices_issued',
  '/invoices-received': 'nav.invoices_received',
  '/clients': 'nav.clients',
  '/clients/statistics': 'nav.client_statistics',
  '/suppliers': 'nav.suppliers',
  '/suppliers/statistics': 'nav.supplier_statistics',
};

function getIcon(name: string): React.ReactElement {
  return ICON_MAP[name] || <DashboardIcon />;
}

export const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, logout } = useAuth();
  const { can, canView, isAdmin } = usePermissions();
  const { mode, toggleTheme } = useThemeMode();
  const { t, i18n } = useTranslation();
  const licenseExpired = useAuthStore((s) => s.licenseExpired);

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [expandedNav, setExpandedNav] = useState<string | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const sidebarWidth = drawerOpen ? LAYOUT.SIDEBAR_WIDTH : LAYOUT.SIDEBAR_COLLAPSED_WIDTH;
  const currentLang = AVAILABLE_LANGUAGES.find((l) => l.code === i18n.language) || AVAILABLE_LANGUAGES[0];

  // Logo entreprise résolu (fallback vers logo FNE-Bridge)
  const companyLogoUrl = company?.logo_url
    ? (company.logo_url.startsWith('http')
        ? company.logo_url
        : company.logo_url)
    : null;

  // Charger le compteur de notifications non lues
  useEffect(() => {
    notificationService.list({ limit: 1 })
      .then((res) => {
        const count = res.data?.data?.unread_count ?? 0;
        setUnreadCount(count);
      })
      .catch(() => setUnreadCount(0));
    // Refresh every 60s
    const interval = setInterval(() => {
      notificationService.list({ limit: 1 })
        .then((res) => setUnreadCount(res.data?.data?.unread_count ?? 0))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleNavClick = (item: NavItem, e?: React.MouseEvent) => {
    // Block navigation when license expired (except settings)
    if (licenseExpired && item.path !== PROTECTED_ROUTES.SETTINGS) {
      e?.preventDefault();
      return;
    }
    if (item.children) {
      setExpandedNav(expandedNav === item.path ? null : item.path);
      e?.preventDefault();
    } else {
      e?.preventDefault();
      navigate(item.path);
      if (isMobile) setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setLangMenuAnchor(null);
  };

  const isActive = (path: string) => location.pathname === path;

  const filteredNav = MAIN_NAV.filter((item) => {
    // Role-based check
    if (item.roles && !(user?.role && item.roles.includes(user.role))) return false;
    // Permission-based check (admin bypasses via canView)
    if (item.permSection && !canView(item.permSection)) return false;
    // Action-based check (e.g., 'clients', 'fournisseurs')
    if (item.permAction && item.permSection && !can(item.permSection, item.permAction)) return false;
    return true;
  });

  // --- Sidebar content ---
  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo sidebar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: drawerOpen ? 'space-between' : 'center',
        px: 2, height: LAYOUT.HEADER_HEIGHT, borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        {drawerOpen && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => navigate(PROTECTED_ROUTES.DASHBOARD)}
          >
            {companyLogoUrl ? (
              <Box
                component="img"
                src={companyLogoUrl}
                alt={company?.raison_sociale || 'Logo'}
                sx={{
                  width: 32, height: 32, borderRadius: '8px', objectFit: 'contain',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                }}
              />
            ) : (
              <Box sx={{
                width: 32, height: 32, borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: '0.75rem', color: '#FFFFFF',
              }}>
                FNE
              </Box>
            )}
            <Typography variant="subtitle1" fontWeight={800} color="white" noWrap>
              {companyLogoUrl ? (company?.raison_sociale || 'Bridge') : 'Bridge'}
            </Typography>
          </Box>
        )}
        {!drawerOpen && (
          <Tooltip title={company?.raison_sociale || 'FNE-Bridge'} placement="right">
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(PROTECTED_ROUTES.DASHBOARD)}
            >
              {companyLogoUrl ? (
                <Box
                  component="img"
                  src={companyLogoUrl}
                  alt={company?.raison_sociale || 'Logo'}
                  sx={{
                    width: 28, height: 28, borderRadius: '6px', objectFit: 'contain',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                  }}
                />
              ) : (
                <Box sx={{
                  width: 28, height: 28, borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 900, fontSize: '0.6rem', color: '#FFFFFF',
                }}>
                  FNE
                </Box>
              )}
            </Box>
          </Tooltip>
        )}
        <IconButton onClick={() => setDrawerOpen(!drawerOpen)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {/* License expired warning banner */}
      {licenseExpired && drawerOpen && (
        <Box sx={{
          mx: 1, mt: 1, mb: 0.5, px: 1.5, py: 1,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(211,47,47,0.25), rgba(211,47,47,0.15))',
          border: '1px solid rgba(211,47,47,0.4)',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <WarningAmberIcon sx={{ color: '#FF8A80', fontSize: 20 }} />
          <Typography variant="caption" sx={{ color: '#FFCDD2', fontWeight: 600, lineHeight: 1.3 }}>
            Licence expirée
          </Typography>
        </Box>
      )}

      {/* Navigation */}
      <List sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
        {filteredNav.map((item) => {
          const navLabel = NAV_I18N_MAP[item.path] ? t(NAV_I18N_MAP[item.path]) : item.label;
          const isDisabledByLicense = licenseExpired && item.path !== PROTECTED_ROUTES.SETTINGS;
          return (
            <React.Fragment key={item.path}>
              <Tooltip title={!drawerOpen ? navLabel : (isDisabledByLicense ? 'Licence expirée' : '')} placement="right" arrow>
                <ListItemButton
                  component="a"
                  href={item.path}
                  onClick={(e: React.MouseEvent) => handleNavClick(item, e)}
                  selected={isActive(item.path)}
                  disabled={isDisabledByLicense}
                  sx={{
                    mx: 1, borderRadius: 2, mb: 0.5,
                    color: isDisabledByLicense ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    pointerEvents: isDisabledByLicense ? 'none' : 'auto',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: '#FFFFFF',
                      '& .MuiListItemIcon-root': { color: '#FFFFFF' },
                    },
                    '&:hover': { backgroundColor: isDisabledByLicense ? 'transparent' : 'rgba(255,255,255,0.08)' },
                    justifyContent: drawerOpen ? 'initial' : 'center',
                    px: drawerOpen ? 2 : 1.5,
                    '&.Mui-disabled': {
                      opacity: 0.35,
                      color: 'rgba(255,255,255,0.25)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: drawerOpen ? 40 : 'auto' }}>
                    {getIcon(item.icon)}
                  </ListItemIcon>
                  {drawerOpen && <ListItemText primary={navLabel} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />}
                  {drawerOpen && item.children && (expandedNav === item.path ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
                </ListItemButton>
              </Tooltip>

              {item.children && drawerOpen && (
                <Collapse in={expandedNav === item.path} timeout="auto">
                  <List disablePadding>
                    {item.children
                      .filter((child) => {
                        if (child.roles && !(user?.role && child.roles.includes(user.role))) return false;
                        if (child.permSection && !canView(child.permSection)) return false;
                        if (child.permAction && child.permSection && !can(child.permSection, child.permAction)) return false;
                        return true;
                      })
                      .map((child) => {
                      const childLabel = CHILD_I18N_MAP[child.path] ? t(CHILD_I18N_MAP[child.path]) : child.label;
                      return (
                        <ListItemButton
                          component="a"
                          href={child.path}
                          key={child.path}
                          onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(child.path); if (isMobile) setDrawerOpen(false); }}
                          selected={isActive(child.path)}
                          sx={{
                            mx: 1, ml: 4, borderRadius: 2, mb: 0.25,
                            color: 'rgba(255,255,255,0.6)',
                            textDecoration: 'none',
                            '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' },
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                          }}
                        >
                          <ListItemText primary={childLabel} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
              {item.dividerAfter && drawerOpen && (
                <Divider sx={{ mx: 2, my: 1, borderColor: 'rgba(255,255,255,0.12)' }} />
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Logo entreprise ou FNE-Bridge entre navigation et company info */}
      {drawerOpen && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 2, px: 2 }}>
          <Box
            component="img"
            src={companyLogoUrl || logoFneBridge}
            alt={companyLogoUrl ? (company?.raison_sociale || 'Logo entreprise') : 'FNE-Bridge'}
            sx={{
              width: companyLogoUrl ? 120 : 100,
              height: 'auto',
              maxHeight: 60,
              objectFit: 'contain',
              opacity: 0.85,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
              borderRadius: companyLogoUrl ? '8px' : 0,
            }}
          />
        </Box>
      )}

      {/* Company info */}
      {drawerOpen && company && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="caption" color="rgba(255,255,255,0.5)" noWrap>
            {company.raison_sociale}
          </Typography>
          <Typography variant="caption" display="block" color="rgba(255,255,255,0.4)" noWrap>
            NCC: {company.ncc}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* --- Sidebar --- */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: isMobile ? LAYOUT.SIDEBAR_WIDTH : sidebarWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? LAYOUT.SIDEBAR_WIDTH : sidebarWidth,
            backgroundColor: '#1B5E20',
            color: '#FFFFFF',
            transition: 'width 0.2s ease-in-out',
            overflowX: 'hidden',
            borderRight: 'none',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* --- Main area --- */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* ================================================================ */}
        {/* BARRE DE NAVIGATION SUPÉRIEURE                                   */}
        {/* Logo FNE | App name | ---- spacer ---- | 🔔 | 🌐 | 👤          */}
        {/* ================================================================ */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
          }}
        >
          <Toolbar sx={{ height: LAYOUT.HEADER_HEIGHT, gap: 1 }}>
            {/* Hamburger mobile */}
            {isMobile && (
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ mr: 0.5 }}>
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo FNE-Bridge cliquable (retour tableau de bord) */}
            <Box
              onClick={() => navigate(PROTECTED_ROUTES.DASHBOARD)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                '&:hover': { opacity: 0.85 },
                transition: 'opacity 0.2s',
              }}
            >
              <Box
                component="img"
                src={logoFneBridge}
                alt="FNE-Bridge"
                sx={{
                  width: 36, height: 36, borderRadius: '10px', objectFit: 'contain',
                  boxShadow: '0 2px 8px rgba(27, 94, 32, 0.3)',
                }}
              />
              {!isMobile && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} color="text.primary">
                    FNE-Bridge
                  </Typography>
                  <Typography variant="caption" color="text.secondary" lineHeight={1} sx={{ fontSize: '0.65rem' }}>
                    {t('app.description')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Dark/Light mode */}
            <Tooltip title={mode === 'light' ? t('header.dark_mode') : t('header.light_mode')}>
              <IconButton onClick={toggleTheme} size="small" sx={{ color: 'text.secondary' }}>
                {mode === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title={t('header.notifications')}>
              <IconButton
                size="small"
                onClick={() => navigate(PROTECTED_ROUTES.NOTIFICATIONS)}
                sx={{ color: 'text.secondary' }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem', minWidth: 18, height: 18,
                      ...(unreadCount > 0 && {
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(244,67,54,0.4)' },
                          '70%': { boxShadow: '0 0 0 6px rgba(244,67,54,0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(244,67,54,0)' },
                        },
                      }),
                    },
                  }}
                >
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Sélecteur de langue */}
            <Tooltip title={t('header.language')}>
              <Chip
                icon={<LanguageIcon sx={{ fontSize: '1rem !important' }} />}
                label={currentLang.flag}
                size="small"
                variant="outlined"
                onClick={(e) => setLangMenuAnchor(e.currentTarget)}
                sx={{
                  cursor: 'pointer', height: 32, borderRadius: '8px',
                  '& .MuiChip-label': { px: 0.5, fontSize: '1rem' },
                  '& .MuiChip-icon': { ml: 0.5 },
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              />
            </Tooltip>

            <Menu
              anchorEl={langMenuAnchor}
              open={Boolean(langMenuAnchor)}
              onClose={() => setLangMenuAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{ sx: { mt: 1, minWidth: 180, borderRadius: 2 } }}
            >
              {AVAILABLE_LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  selected={i18n.language === lang.code}
                  sx={{ gap: 1.5 }}
                >
                  <Typography sx={{ fontSize: '1.1rem', width: 24, textAlign: 'center' }}>{lang.flag}</Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>{lang.label}</Typography>
                  {i18n.language === lang.code && <CheckIcon fontSize="small" color="primary" />}
                </MenuItem>
              ))}
            </Menu>

            {/* Menu utilisateur */}
            <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ ml: 0.5 }}>
              <Avatar
                sx={{
                  width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{ sx: { mt: 1, minWidth: 220, borderRadius: 2 } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user?.prenom} {user?.nom}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
                {user?.role && (
                  <Chip
                    label={t(`users.role_${user.role}`)}
                    size="small"
                    color={user.role === 'admin' ? 'error' : user.role === 'manager' ? 'warning' : 'default'}
                    sx={{
                      mt: 0.5, height: 20, fontSize: '0.7rem',
                      ...(user.role === 'caisse' && { bgcolor: '#1976d2', color: '#fff' }),
                      ...(user.role === 'facturation' && { bgcolor: '#2e7d32', color: '#fff' }),
                    }}
                  />
                )}
              </Box>
              <Divider />
              <MenuItem onClick={() => { setUserMenuAnchor(null); navigate(PROTECTED_ROUTES.PROFILE); }}>
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                {t('header.profile')}
              </MenuItem>
              <MenuItem onClick={() => { setUserMenuAnchor(null); navigate(PROTECTED_ROUTES.SETTINGS); }}>
                <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                {t('header.settings')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setUserMenuAnchor(null); logout(); }} sx={{ color: 'error.main' }}>
                <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                {t('header.logout')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            maxWidth: LAYOUT.CONTENT_MAX_WIDTH,
            width: '100%',
            mx: 'auto',
          }}
          className="animate-fade-in"
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
